import { I18nService } from '@core/i18n-legacy';
import { MemberEmailContext, TeamMemberStatus } from '@core/tellent-types';
import type {
  BadgeColor,
  IconName,
  RtButtonColorContextVariant,
  RtButtonVariant,
  RtInfoboxButtonVariant,
  RtWalkthroughButtonVariant,
} from '@core/user-interface-legacy';

export type StatusBadgeConfig = {
  text: string;
  color: BadgeColor;
};

export type StatusActionConfig = {
  text: string;
  icon: IconName;
  variant:
    | RtButtonVariant
    | RtInfoboxButtonVariant
    | RtButtonColorContextVariant
    | RtWalkthroughButtonVariant;
};

export type MemberStatusConfig = {
  badge: StatusBadgeConfig;
  action: StatusActionConfig;
};

function createNotInvitedWithWorkEmailBadgeConfig(i18n: I18nService): StatusBadgeConfig {
  return {
    text: i18n.translate('tlc.members.edit_member.invitation_status.not_invited.badge'),
    color: 'draft',
  };
}

function createNotInvitedWithPersonalEmailBadgeConfig(i18n: I18nService): StatusBadgeConfig {
  return {
    text: i18n.translate('tlc.members.edit_member.invitation_status.not_invited.badge'),
    color: 'draft',
  };
}

function createInvitedBadgeConfig(i18n: I18nService): StatusBadgeConfig {
  return {
    text: i18n.translate('tlc.members.edit_member.invitation_status.invited.badge'),
    color: 'requested-tint',
  };
}

function createActiveBadgeConfig(i18n: I18nService): StatusBadgeConfig {
  return {
    text: i18n.translate('tlc.members.edit_member.invitation_status.active.badge'),
    color: 'success-tint',
  };
}

function createDeactivatedBadgeConfig(i18n: I18nService): StatusBadgeConfig {
  return {
    text: i18n.translate('tlc.members.edit_member.invitation_status.deactivated.badge'),
    color: 'rejected-tint',
  };
}

function createTemporaryPasswordBadgeConfig(i18n: I18nService): StatusBadgeConfig {
  return {
    text: i18n.translate('tlc.members.edit_member.invitation_status.temporary_password.badge'),
    color: 'requested-tint',
  };
}

function createInviteActionConfig(i18n: I18nService): StatusActionConfig {
  return {
    text: i18n.translate('tlc.members.edit_member.header_actions.invite'),
    icon: 'send',
    variant: 'outline',
  };
}

function createStartPreonboardingActionConfig(i18n: I18nService): StatusActionConfig {
  return {
    text: i18n.translate('tlc.members.edit_member.header_actions.start_preonboarding'),
    icon: 'preonboarding-journey',
    variant: 'outline',
  };
}

function createResendInvitationActionConfig(i18n: I18nService): StatusActionConfig {
  return {
    text: i18n.translate('tlc.members.edit_member.header_actions.resend_invitation'),
    icon: 'send',
    variant: 'outline',
  };
}

function createReactivateActionConfig(i18n: I18nService): StatusActionConfig {
  return {
    text: i18n.translate('tlc.members.edit_member.header_actions.reactivate'),
    icon: 'member-activate',
    variant: 'outline',
  };
}

export function canStartPreonboarding(
  status: TeamMemberStatus,
  emailContext: MemberEmailContext,
  usersWithoutEmails: boolean | undefined,
  orgHasGrowStandalone: boolean,
  orgHasCoreHrInactive: boolean,
): boolean {
  const { hasWorkEmail, hasPersonalEmail } = emailContext;

  return (
    status === TeamMemberStatus.NotInvited &&
    !!usersWithoutEmails &&
    hasPersonalEmail &&
    !hasWorkEmail &&
    (!orgHasGrowStandalone || !orgHasCoreHrInactive)
  );
}

// eslint-disable-next-line max-params-no-constructor/max-params-no-constructor
export function getMemberStatusConfig(
  status: TeamMemberStatus,
  emailContext: MemberEmailContext,
  i18n: I18nService,
  usersWithoutEmails = false,
  orgHasGrowStandalone: boolean,
  orgHasCoreHrInactive = false,
): Partial<MemberStatusConfig> {
  switch (status) {
    case TeamMemberStatus.NotInvited:
      if (
        canStartPreonboarding(
          status,
          emailContext,
          usersWithoutEmails,
          orgHasGrowStandalone,
          orgHasCoreHrInactive,
        )
      ) {
        return {
          badge: createNotInvitedWithPersonalEmailBadgeConfig(i18n),
          action: createStartPreonboardingActionConfig(i18n),
        };
      }
      return {
        badge: createNotInvitedWithWorkEmailBadgeConfig(i18n),
        action: createInviteActionConfig(i18n),
      };

    case TeamMemberStatus.Invited:
      return {
        badge: createInvitedBadgeConfig(i18n),
        action: createResendInvitationActionConfig(i18n),
      };

    case TeamMemberStatus.Active:
      return {
        badge: createActiveBadgeConfig(i18n),
      };

    case TeamMemberStatus.TemporaryPassword:
      if (usersWithoutEmails) {
        return {
          badge: createTemporaryPasswordBadgeConfig(i18n),
        };
      }
      return {};

    case TeamMemberStatus.Deactivated:
      return {
        badge: createDeactivatedBadgeConfig(i18n),
        action: createReactivateActionConfig(i18n),
      };

    case TeamMemberStatus.PausedAccount:
      return {};

    default:
      return {};
  }
}
